import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle } from "lucide-react";
import AbstractShape from "@/components/AbstractShape";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { goToIntake } from "@/lib/intake";
import useSEO from "@/lib/useSEO";

// 5-step patient process. Kept in a plain array so the visible copy is
// easy to update without touching layout code.
const STEPS = [
  {
    n: "01",
    title: "Choose your wellness goal",
    body: "Select the area you're interested in, such as weight management, energy support, metabolic support, antioxidant support, vitality, recovery, or skin care support.",
  },
  {
    n: "02",
    title: "Complete a secure online intake",
    body: "You'll answer questions about your health history, medications, allergies, goals, and other information needed for clinical review.",
  },
  {
    n: "03",
    title: "A licensed clinician reviews your information",
    body: "A clinician will determine whether treatment may be appropriate. They may request more information, recommend a different option, or determine that treatment is not appropriate.",
  },
  {
    n: "04",
    title: "If prescribed, your treatment is fulfilled by a licensed U.S. pharmacy",
    body: "Prescription treatments are only issued when medically appropriate. If prescribed, your treatment is sent to a licensed U.S. pharmacy for fulfillment.",
  },
  {
    n: "05",
    title: "Your treatment ships directly to you",
    body: "Shipping timing and product availability may vary based on clinician review, pharmacy processing, state laws, product availability, and your location.",
  },
];

export default function WhatToExpect() {
  useSEO({
    title: "What to Expect | Melbi Wellness",
    description:
      "See what to expect before treatment with Melbi Wellness. A licensed clinician reviews your intake before any prescription treatment is issued or shipped.",
    url: "https://melbiwellness.com/what-to-expect",
  });

  return (
    <div data-testid="what-to-expect-page" className="bg-[#FAF6EF]">
      {/* HERO */}
      <section className="relative">
        <AbstractShape
          variant="gradient-soft-sage"
          colors={["#D4DCD7", "#E8D5C4", "#F5E6D3"]}
          className="absolute inset-0"
          testId="what-to-expect-hero-bg"
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 md:pt-24 md:pb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            What to Expect
          </p>
          <h1
            data-testid="what-to-expect-h1"
            className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight text-[#2C2B29] max-w-3xl"
          >
            What to expect{" "}
            <span className="italic text-[#C96A4E]">before treatment.</span>
          </h1>
          <p className="mt-5 text-[#2C2B29] font-display text-lg md:text-xl leading-snug max-w-2xl">
            A licensed clinician reviews your intake before any prescription
            treatment is issued or shipped.
          </p>
          <p className="mt-3 text-[#5E5C58] leading-relaxed max-w-2xl">
            Melbi is designed to help you understand the process, what
            information may be reviewed, and why treatment is only provided
            when medically appropriate.
          </p>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10 md:pt-6 md:pb-14">
        <h2
          data-testid="process-heading"
          className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#2C2B29]"
        >
          How the process works
        </h2>

        <ol className="mt-6 md:mt-8 space-y-3 md:space-y-4">
          {STEPS.map((s) => (
            <li
              key={s.n}
              data-testid={`process-step-${s.n}`}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-5 md:p-6 flex gap-4 md:gap-5"
            >
              <span
                aria-hidden="true"
                className="shrink-0 font-display text-xl md:text-2xl font-bold text-[#C96A4E] tabular-nums"
              >
                {s.n}
              </span>
              <div>
                <h3 className="font-display text-lg md:text-xl font-semibold text-[#2C2B29] leading-snug">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm md:text-base text-[#5E5C58] leading-relaxed">
                  {s.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ELIGIBILITY NOTES */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14">
        <div
          data-testid="eligibility-notes"
          className="bg-white rounded-2xl border border-[#E8E5DF] p-6 md:p-8"
        >
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#2C2B29]">
            Important eligibility notes
          </h2>
          <p className="mt-4 text-[#5E5C58] leading-relaxed">
            Not every patient will qualify. Eligibility may vary based on your
            health history, current medications, medication allergies,
            pregnancy or breastfeeding status, age requirements, state laws,
            product availability, and treatment-specific requirements.
          </p>
          <p className="mt-3 text-[#5E5C58] leading-relaxed">
            There is no automatic approval. Prescription treatments are only
            issued after licensed clinician review when medically appropriate.
          </p>
        </div>
      </section>

      {/* COMPOUNDED MEDICATIONS ACCORDION */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 md:pb-12">
        <Accordion
          type="single"
          collapsible
          className="bg-white rounded-2xl border border-[#E8E5DF] px-5 md:px-6"
          data-testid="compounded-accordion"
        >
          <AccordionItem value="compounded" className="border-b-0">
            <AccordionTrigger
              data-testid="compounded-accordion-trigger"
              className="font-display text-base md:text-lg font-semibold text-[#2C2B29] hover:no-underline py-5"
            >
              About compounded medications
            </AccordionTrigger>
            <AccordionContent
              data-testid="compounded-accordion-content"
              className="text-sm md:text-base text-[#5E5C58] leading-relaxed space-y-3 pb-5"
            >
              <p>
                Some Melbi treatment options may include compounded medications
                when prescribed. Compounded medications are not FDA-approved.
                The FDA does not review compounded medications for safety,
                effectiveness, or quality before they are dispensed.
              </p>
              <p>
                Compounded medications are prepared by a licensed compounding
                pharmacy for an individual patient based on a prescription
                from a licensed clinician. They are not the same as,
                equivalent to, or interchangeable with FDA-approved brand-name
                medications.
              </p>
              <p>
                Treatment options depend on clinical appropriateness,
                medication availability, state laws, pharmacy requirements,
                and clinician review.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      {/* EMERGENCY DISCLAIMER */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14">
        <div
          data-testid="emergency-disclaimer"
          className="flex items-start gap-3 rounded-2xl border border-[#E8D5C4] bg-[#F5E6D3]/40 px-5 py-4"
        >
          <AlertTriangle size={18} className="text-[#C96A4E] mt-0.5 shrink-0" />
          <p className="text-sm text-[#5E5C58] leading-relaxed">
            <span className="font-semibold text-[#2C2B29]">
              Not for emergencies.
            </span>{" "}
            Melbi is not emergency care and does not replace primary care. For
            emergencies, please call{" "}
            <a
              href="tel:911"
              className="font-semibold text-[#C96A4E] hover:text-[#B35A40] transition-colors"
            >
              911
            </a>{" "}
            or visit your nearest emergency department.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 text-center">
        <h2
          data-testid="cta-heading"
          className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29]"
        >
          Have questions before you start?
        </h2>
        <p className="mt-3 text-[#5E5C58]">
          Review the FAQ or begin the online intake to see what treatment
          options may be available to you.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/faq"
            data-testid="cta-read-faq"
            className="inline-flex items-center justify-center bg-white text-[#2C2B29] rounded-full px-8 py-4 text-base font-semibold border border-[#E8E5DF] hover:bg-[#F3F1EC] transition-colors"
          >
            Read the FAQ
          </Link>
          <button
            type="button"
            data-testid="cta-start-intake"
            onClick={goToIntake}
            className="inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-4 text-base font-semibold hover:bg-[#B35A40] transition-colors"
          >
            Start intake
            <ArrowRight size={18} className="ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
}
